import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2,
  MapPin,
  DollarSign,
  Calendar,
  ExternalLink,
  Trash2,
  Clock,
  ArrowRight,
  FileText,
  Loader2,
  Save,
  Sparkles,
  Zap,
  AlertTriangle,
  Search,
  Rocket,
  Download,
  Eye,
  Edit3,
  Check,
  X,
  Mail
} from "lucide-react";
import { format, isValid, parseISO } from "date-fns";
import ResumeTailorModal from "./ResumeTailorModal";
import CoverLetterTailorModal from "./CoverLetterTailorModal";
import { Input } from "@/components/ui/input";

const formatDate = (dateString, pattern = 'MMMM d, yyyy') => {
  if (!dateString) return 'N/A';
  try {
    const date = parseISO(dateString);
    if (isValid(date)) {
      return format(date, pattern);
    }
    return 'N/A';
  } catch {
    return 'N/A';
  }
};

const STATUS_OPTIONS = [
  { value: "SAVED", label: "Saved", color: "#94A3B8" },
  { value: "APPLIED", label: "Applied", color: "#A3C3D8" },
  { value: "INTERVIEWING", label: "Interviewing", color: "#F6AD55" },
  { value: "OFFER", label: "Offer", color: "#68D391" },
  { value: "REJECTED", label: "Rejected", color: "#FC8181" }
];

export const JobDetailsModal = ({ application, onClose, onDelete, onStatusUpdate }) => {
  const { token } = useAuth();
  const [timeline, setTimeline] = useState([]);
  const [notes, setNotes] = useState(application.notes || "");
  const [loading, setLoading] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showResumeTailor, setShowResumeTailor] = useState(false);
  const [showCoverLetterTailor, setShowCoverLetterTailor] = useState(false);
  const [hasTailoredResume, setHasTailoredResume] = useState(false);
  const [hasCoverLetter, setHasCoverLetter] = useState(false);
  const [checkingResume, setCheckingResume] = useState(false);
  const [checkingCoverLetter, setCheckingCoverLetter] = useState(false);
  const [showUrlEdit, setShowUrlEdit] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [updatingUrl, setUpdatingUrl] = useState(false);
  const [currentJobUrl, setCurrentJobUrl] = useState(application.job?.url || "");

  const { job } = application;

  useEffect(() => {
    fetchTimeline();
    checkForTailoredResume();
    checkForCoverLetter();
  }, [application.id]);

  const checkForTailoredResume = async () => {
    try {
      setCheckingResume(true);
      const response = await axios.post(
        `${API}/autofill/data`,
        { application_id: application.id, ats_provider: "check" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHasTailoredResume(response.data.is_tailored_pdf === true);
    } catch (error) {
      console.error("Failed to check resume status:", error);
      setHasTailoredResume(false);
    } finally {
      setCheckingResume(false);
    }
  };

  const checkForCoverLetter = async () => {
    try {
      setCheckingCoverLetter(true);
      const response = await axios.get(
        `${API}/applications/${application.id}/cover-letter/check`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHasCoverLetter(response.data.has_cover_letter === true);
    } catch (error) {
      console.error("Failed to check cover letter status:", error);
      setHasCoverLetter(false);
    } finally {
      setCheckingCoverLetter(false);
    }
  };

  const handlePreviewAutofillResume = async () => {
    try {
      toast.info("Downloading resume...");
      const response = await axios.get(
        `${API}/applications/${application.id}/tailored-resume/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const filename = hasTailoredResume 
        ? `resume-${job?.company || 'job'}.pdf`
        : `base_resume_${job?.company || 'job'}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(hasTailoredResume ? "Tailored resume downloaded!" : "Resume downloaded!");
    } catch (error) {
      console.error("Preview failed:", error);
      if (error.response?.status === 404) {
        toast.error("No resume found. Please upload a resume or generate a tailored one first.");
      } else {
        toast.error("Failed to download resume");
      }
    }
  };

  const fetchTimeline = async () => {
    try {
      const response = await axios.get(
        `${API}/applications/${application.id}/timeline`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTimeline(response.data);
    } catch (error) {
      console.error("Failed to fetch timeline:", error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === application.status) return;
    
    setLoading(true);
    try {
      await axios.patch(
        `${API}/applications/${application.id}/status`,
        { status: newStatus, trigger_type: "manual" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status updated to ${newStatus.toLowerCase()}`);
      onStatusUpdate(application.id, newStatus);
      fetchTimeline();
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUrl = async () => {
    if (!newUrl.trim()) {
      toast.error("Please enter a valid URL");
      return;
    }
    
    setUpdatingUrl(true);
    try {
      await axios.patch(
        `${API}/applications/${application.id}/url`,
        { url: newUrl.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Job URL updated!");
      setCurrentJobUrl(newUrl.trim());
      setShowUrlEdit(false);
      // Update the job object so links work immediately
      if (job) {
        job.url = newUrl.trim();
      }
    } catch (error) {
      toast.error("Failed to update URL");
    } finally {
      setUpdatingUrl(false);
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.patch(
        `${API}/applications/${application.id}/notes`,
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Notes saved");
    } catch (error) {
      toast.error("Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this job?")) return;
    
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/applications/${application.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Job deleted");
      onDelete(application.id);
    } catch (error) {
      toast.error("Failed to delete job");
      setDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    return STATUS_OPTIONS.find(s => s.value === status)?.color || "#94A3B8";
  };

  const getTriggerLabel = (trigger) => {
    const labels = {
      extension: "Via Extension",
      manual: "Manual",
      drag_drop: "Drag & Drop",
      email: "Email Detection"
    };
    return labels[trigger] || trigger;
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="job-details-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div 
              className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-lg font-semibold"
              style={{ backgroundColor: getStatusColor(application.status) }}
            >
              {job?.company?.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-[#2D3748]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {job?.title}
              </h2>
              <p className="text-sm text-[#94A3B8] flex items-center gap-1">
                <Building2 className="h-4 w-4" />
                {job?.company}
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Status & Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm text-[#555F67]">Status:</span>
              <Select value={application.status} onValueChange={handleStatusChange} disabled={loading}>
                <SelectTrigger className="w-40" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(status => (
                    <SelectItem key={status.value} value={status.value}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-2 h-2 rounded-full" 
                          style={{ backgroundColor: status.color }}
                        />
                        {status.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowResumeTailor(true)}
                className="text-[#4A728D] border-[#A3C3D8] hover:bg-[#A3C3D8]/10"
                data-testid="generate-resume-btn"
              >
                <Sparkles className="h-4 w-4 mr-1" />
                Tailor Resume
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCoverLetterTailor(true)}
                className="text-[#4A728D] border-[#A3C3D8] hover:bg-[#A3C3D8]/10"
                data-testid="generate-cover-letter-btn"
              >
                <Mail className="h-4 w-4 mr-1" />
                {checkingCoverLetter ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <span className="text-xs">
                    {hasCoverLetter ? "View CL" : "Cover Letter"}
                  </span>
                )}
              </Button>
              {/* Apply with Autofill button - opens ATS page with application context */}
              {job?.url && !job.url.startsWith('manual://') && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePreviewAutofillResume}
                    className="text-[#4A728D] border-[#A3C3D8] hover:bg-[#A3C3D8]/10"
                    data-testid="preview-resume-btn"
                    title={hasTailoredResume ? "Preview tailored resume that will be attached" : "Preview resume that will be attached"}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    {checkingResume ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <span className="text-xs">
                        {hasTailoredResume ? "Tailored" : "Base"}
                      </span>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      // Store application context for autofill in multiple places
                      const appId = application.id;
                      
                      // 1. Store in localStorage (for same-origin)
                      localStorage.setItem('oneway_autofill_app_id', appId);
                      
                      // 2. Try to communicate with extension via externally_connectable
                      // This allows the web page to send messages to the extension
                      try {
                        // Try to find the extension ID and send message
                        // The extension ID is in chrome.runtime.id when accessed from extension context
                        // For web pages, we need to use a known extension ID or the extension listens via external messages
                        // eslint-disable-next-line no-undef
                        if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
                          // This will work if extension is installed and externally_connectable is set up
                          // eslint-disable-next-line no-undef
                          chrome.runtime.sendMessage(
                            { action: 'setAutofillAppId', applicationId: appId },
                            (response) => {
                              console.log('Extension response:', response);
                            }
                          );
                        }
                      } catch (e) {
                        console.log('Extension communication not available:', e);
                      }
                      
                      // 3. Also dispatch a custom event that any content script could catch
                      window.postMessage({ 
                        type: 'ONEWAY_SET_APP_ID', 
                        applicationId: appId 
                      }, '*');
                      
                      // Determine target URL - prefer current (potentially updated) URL
                      let applyUrl = currentJobUrl || job.original_url || job.url;
                      
                      // Add application context to URL using BOTH query param AND hash
                      // Some ATS strip query params but keep hash
                      const separator = applyUrl.includes('?') ? '&' : '?';
                      const targetUrl = `${applyUrl}${separator}oneway_app_id=${appId}#oneway_app_id=${appId}`;
                      
                      console.log('Opening URL with app ID:', targetUrl);
                      window.open(targetUrl, '_blank');
                      toast.success(hasTailoredResume 
                        ? 'Opening with tailored resume ready!' 
                        : 'Opening application page with autofill ready!');
                    }}
                    className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                    data-testid="apply-autofill-btn"
                  >
                    <Rocket className="h-4 w-4 mr-1" />
                    Apply with Autofill
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleDelete}
                disabled={deleting}
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                data-testid="delete-job-btn"
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Job Details */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            {job?.location && (
              <div className="flex items-center gap-2 text-sm text-[#555F67]">
                <MapPin className="h-4 w-4 text-[#94A3B8]" />
                {job.location}
              </div>
            )}
            {job?.salary && (
              <div className="flex items-center gap-2 text-sm text-[#555F67]">
                <DollarSign className="h-4 w-4 text-[#94A3B8]" />
                {job.salary}
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-[#555F67]">
              <Calendar className="h-4 w-4 text-[#94A3B8]" />
              Added {formatDate(application.created_at)}
            </div>
            {job?.url && !job.url.startsWith('manual://') && (
              job.platform === 'Monster' ? (
                <div className="space-y-2">
                  {/* Monster warning */}
                  <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                    <AlertTriangle className="h-3 w-3" />
                    <span>Monster links are search-based approximations</span>
                  </div>
                  {/* Search link */}
                  <a
                    href={job.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-[#4A728D] hover:text-[#355870] transition-colors"
                  >
                    <Search className="h-4 w-4" />
                    Search on Monster
                  </a>
                  {/* Original page link (if available) */}
                  {job.original_url && job.original_url !== job.url && (
                    <a
                      href={job.original_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-[#4A728D] hover:text-[#355870] transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Original Page
                      <span className="text-xs text-emerald-600">(if still active)</span>
                    </a>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Warning for Glassdoor jobs with search URLs (not proper job URLs) */}
                  {job?.platform?.toLowerCase() === 'glassdoor' && 
                   (currentJobUrl || job?.url)?.includes('?sc.keyword=') && 
                   !(currentJobUrl || job?.url)?.includes('partner/jobListing') &&
                   !(currentJobUrl || job?.url)?.includes('jobListingId') && (
                    <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium">This link opens a search page, not the exact job.</p>
                        {!showUrlEdit ? (
                          <button 
                            onClick={() => {
                              setNewUrl(currentJobUrl || job?.url || "");
                              setShowUrlEdit(true);
                            }}
                            className="mt-1 text-amber-800 underline hover:text-amber-900 flex items-center gap-1"
                          >
                            <Edit3 className="h-3 w-3" />
                            Click to manually update the URL
                          </button>
                        ) : (
                          <div className="mt-2 space-y-2">
                            <Input
                              value={newUrl}
                              onChange={(e) => setNewUrl(e.target.value)}
                              placeholder="Paste the correct job URL here..."
                              className="text-xs h-8"
                            />
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                onClick={handleUpdateUrl}
                                disabled={updatingUrl}
                                className="h-6 text-xs bg-amber-600 hover:bg-amber-700"
                              >
                                {updatingUrl ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                Save
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => setShowUrlEdit(false)}
                                className="h-6 text-xs"
                              >
                                <X className="h-3 w-3" />
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <a
                    href={currentJobUrl || job.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-[#4A728D] hover:text-[#355870] transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View Original Posting
                  </a>
                </div>
              )
            )}
          </div>

          {/* Description */}
          {job?.description && (
            <div>
              <h3 className="text-sm font-medium text-[#2D3748] mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Description
              </h3>
              <div className="bg-slate-50 rounded-lg p-4 text-sm text-[#555F67] whitespace-pre-wrap max-h-40 overflow-y-auto">
                {job.description}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <h3 className="text-sm font-medium text-[#2D3748] mb-2">Notes</h3>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add your notes here..."
              className="min-h-[100px] resize-none"
              data-testid="notes-textarea"
            />
            <Button
              size="sm"
              onClick={handleSaveNotes}
              disabled={savingNotes}
              className="mt-2 bg-[#A3C3D8] hover:bg-[#8FB3C9] text-[#152A38]"
              data-testid="save-notes-btn"
            >
              {savingNotes ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Save className="h-4 w-4 mr-1" />
                  Save Notes
                </>
              )}
            </Button>
          </div>

          {/* Timeline */}
          <div>
            <h3 className="text-sm font-medium text-[#2D3748] mb-4 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Activity Timeline
            </h3>
            <div className="relative">
              {timeline.length > 1 && (
                <div className="timeline-line" />
              )}
              <div className="space-y-4">
                {timeline.map((event, index) => (
                  <div key={event.id} className="flex gap-3">
                    <div 
                      className="timeline-dot bg-white border-2"
                      style={{ borderColor: getStatusColor(event.to_status) }}
                    >
                      <div 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: getStatusColor(event.to_status) }}
                      />
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center gap-2">
                        {event.from_status ? (
                          <>
                            <span 
                              className="text-xs px-2 py-0.5 rounded-full"
                              style={{ 
                                backgroundColor: `${getStatusColor(event.from_status)}20`,
                                color: getStatusColor(event.from_status)
                              }}
                            >
                              {event.from_status}
                            </span>
                            <ArrowRight className="h-3 w-3 text-[#94A3B8]" />
                          </>
                        ) : null}
                        <span 
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{ 
                            backgroundColor: `${getStatusColor(event.to_status)}20`,
                            color: getStatusColor(event.to_status)
                          }}
                        >
                          {event.to_status}
                        </span>
                      </div>
                      <p className="text-xs text-[#94A3B8] mt-1">
                        {formatDate(event.timestamp, 'MMM d, yyyy h:mm a')} • {getTriggerLabel(event.trigger_type)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Resume Tailor Modal */}
      {showResumeTailor && (
        <ResumeTailorModal
          job={job}
          application={application}
          onClose={() => {
            setShowResumeTailor(false);
            // Refresh tailored resume status when modal closes
            checkForTailoredResume();
          }}
          onTailoringComplete={(result) => {
            console.log("Resume tailored:", result);
            // Immediately update the status to show "Tailored"
            setHasTailoredResume(true);
          }}
        />
      )}

      {/* Cover Letter Tailor Modal */}
      {showCoverLetterTailor && (
        <CoverLetterTailorModal
          job={job}
          application={application}
          onClose={() => {
            setShowCoverLetterTailor(false);
            // Refresh cover letter status when modal closes
            checkForCoverLetter();
          }}
          onGenerated={(result) => {
            console.log("Cover letter generated:", result);
            // Immediately update the status to show we have a cover letter
            setHasCoverLetter(true);
          }}
        />
      )}
    </Dialog>
  );
};

export default JobDetailsModal;
